import React, { useState } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton,
    Typography,
    CircularProgress,
    Collapse,
    Stack,
    Paper,
    TextField,
    Button,
    Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { Icon } from '@iconify/react';
import { IHbDeal } from '../../shared/types/HbDeal.type';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useAuth } from '../../hooks/useAuth';
import { TChannel } from '../../shared/types/Channel.type';
import { LoadingButton } from '@mui/lab';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { compact, isEmpty } from 'lodash';
import EmptyState from '../global/EmptyState';

function SendMessage({ templates, commChannels }: { commChannels: TChannel[], templates: IHbDeal['whatsapp_message_templates'] }) {
    const [loadingId, setLoadingId] = useState<number | null>(null);
    const [previews, setPreviews] = useState<Record<number, string>>({});
    const [editedTexts, setEditedTexts] = useState<Record<number, { text_en: string; text_fr: string }>>({});
    const { previewWaMessage, sendWaMessage } = useOnboarding();
    const { auth } = useAuth();
    const BACKOFFICE_LINK = process.env.REACT_APP_BACKOFFICE_URI

    const fetchPreview = async (template: typeof templates[number]) => {
        try {
            setLoadingId(template.id);
            const current = editedTexts[template.id] || { text_en: template.text_en, text_fr: template.text_fr };

            const response = await previewWaMessage.mutateAsync({ text: current.text_en, user: auth['@id'] });
            setPreviews((prev) => ({ ...prev, [template.id]: response.data?.text || "" }));
        } catch (error) {
            toast(t('error:generic'), { type: 'error' })
        } finally {
            setLoadingId(null);
        }
    };

    const handleSend = async (template: typeof templates[number]) => {
        try {
            const current = editedTexts[template.id] || { text_en: template.text_en, text_fr: template.text_fr };
            let communication_channels: string[] = [];
            const own_private_channel = commChannels.find(channel => channel.own_private_channel);
            const groupChannel = commChannels.find(channel => channel?.type === 'WHATSAPP_GROUP');
            communication_channels = compact([own_private_channel, groupChannel].map(channel => channel?.['@id']))



            await sendWaMessage.mutateAsync({
                text_en: current.text_en,
                text_fr: current.text_fr,
                communication_channels
            });
            toast(t('onboarding:msgSuccess'), { type: 'success' })


        } catch (error) {
            toast(t('error:generic'), { type: 'error' })
        }
    };

    const handleTextChange = (id: number, field: 'text_en' | 'text_fr', value: string) => {
        setEditedTexts((prev) => ({
            ...prev,
            [id]: { ...prev[id], [field]: value },
        }));
    };


    return (
        <Box>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                <Typography variant="h6" gutterBottom>
                    {t('onboarding:msgTemplate')}
                </Typography>
                <Button component='a' target='_blank' href={BACKOFFICE_LINK?.concat('#/whatsapp_message_templates')} variant='text' endIcon={<Icon icon={'qlementine-icons:external-link-16'} />}>{t('button:manageTemplates')}</Button>

            </Box>

            <Box
                maxHeight={500}
                overflow="auto"
                border={1}
                borderColor="divider"
                borderRadius={2}
            >
                <List disablePadding>
                    {
                        isEmpty(templates) ? <EmptyState /> :
                            templates.map((tpl, idx) => {
                                const current = editedTexts[tpl.id] || { text_en: tpl.text_en, text_fr: tpl.text_fr };

                                return (
                                    <React.Fragment key={tpl.id}>
                                        <ListItem
                                            alignItems="flex-start"
                                            sx={{
                                                px: 2,
                                                py: 2,
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            {/* Top row: title + send */}
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" fontWeight={600}>
                                                            {tpl.name}
                                                        </Typography>
                                                    }
                                                />
                                            </Stack>

                                            {/* Editable fields */}
                                            <Stack spacing={2} mt={2}>
                                                <TextField
                                                    label="Text (EN)"
                                                    value={current.text_en}
                                                    onChange={(e) => handleTextChange(tpl.id, 'text_en', e.target.value)}
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                />
                                                <TextField
                                                    label="Text (FR)"
                                                    value={current.text_fr}
                                                    onChange={(e) => handleTextChange(tpl.id, 'text_fr', e.target.value)}
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                />
                                            </Stack>

                                            {/* Preview button (below fields, right-aligned) */}
                                            <Stack direction="row" justifyContent="flex-end" mt={1} gap={1}>
                                                <LoadingButton
                                                    loading={loadingId === tpl.id}
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={() => fetchPreview(tpl)}
                                                    startIcon={<Icon icon="hugeicons:message-preview-02" />}
                                                >
                                                    {t('button:preview')}
                                                </LoadingButton>
                                                <LoadingButton
                                                    variant="contained"
                                                    disabled={sendWaMessage.isLoading}
                                                    onClick={() => handleSend(tpl)}
                                                    startIcon={<Icon icon="tabler:message-share" />}
                                                >
                                                    {t('button:send')}
                                                </LoadingButton>
                                            </Stack>

                                            {/* Expanded preview */}
                                            <Collapse in={!!previews[tpl.id]} unmountOnExit>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        mt: 1.5,
                                                        px: 2,
                                                        py: 1,
                                                        fontSize: '0.9rem',
                                                        fontFamily: 'monospace',
                                                        bgcolor: 'background.paper',
                                                        borderRadius: 1,
                                                        whiteSpace: 'pre-wrap',
                                                    }}
                                                >
                                                    {previews[tpl.id]}
                                                </Paper>
                                            </Collapse>
                                        </ListItem>

                                        {
                                            idx < templates.length - 1 && (
                                                <Divider sx={{ bgcolor: 'divider', opacity: 0.2 }} />
                                            )
                                        }
                                    </React.Fragment>
                                );
                            })}
                </List>
            </Box>
        </Box >
    );
}

export default SendMessage;
